export default {
  common: {
    ok: "OK",
    cancel: "Cancel",
    brand: "Forward",
    slogan: "Forward with gratitude.<br>Build with <b>intention.</b>",
    intro: "Every receipt is proof that money moved through your life and business. Forward it with gratitude, stay aware of where your money goes, and attract wealth one intentional decision at a time."
  },

  header: {
    blog: "Blog",
    features: "Features",
    subscription: "Pricing",
    faq: "FAQ",
    contact: "Contact",
    startFreeTrial: "Start Free Trial"
  },
  footer: {
    desc: "Forward every receipt with gratitude. Let AI organize the details while you build a clearer, more intentional relationship with money.",
    productHelp: "Product Help",
    privacyPolicy: "Privacy Policy",
    termsConditions: "Terms & Conditions",
    deleteAccount: "Delete Account",
    contactInfo: "Contact Info",
    email: "Email: support@forwardreceipts.com",
    officeHour: "Office Hour: Mon–Fri, 9am–6pm (GMT+8)",
    copyright: "2026 Impulz Digital Pte Ltd. All rights reserved."
  },
  hero: {
    eyebrow: "A Daily Money Ritual for Freelancers",
    homeIntro: "Every receipt is proof that money moved through your life and business. Forward it with gratitude, stay aware of where your money goes, and attract wealth one intentional decision at a time.",
    appStoreAlt: "Download on the App Store",
    googlePlayAlt: "Get it on Google Play",
    freeTrialTitle: "Free Trial",
    freeTrialDesc: "Start with a completely free plan <br> and explore all core features.",
    startFreeTitle: "Start free",
    startFreeDesc: "Try it before you decide. <br> No pressure. No lock-in.",
    builtForRealLifeTitle: "Built for real life.",
    builtForRealLifeDesc: "Receipts don't stop. Neither should you. Forward it. Forget it.",
    supportTitle: "Support that shows up.",
    supportDesc: "Email us. We're there.",
    oneActionTitle: "One action.",
    oneActionDesc: "Forward the receipt. Done."
  },
  features: {
    featureLabel: "Feature",
    receiptInboxTitle: "Your own receipt inbox",
    receiptInboxDesc1: "No manual uploads required",
    receiptInboxDesc2: "Send one receipt or many at once",
    receiptInboxDesc3: "Works instantly with merchant email receipts",
    receiptInboxSummary: "This is the core habit: forward it and forget it.",
    autoExtractTitle: "Automatic receipt details",
    autoExtractDesc: "We extract what matters automatically:",
    autoExtractMerchant: "merchant",
    autoExtractAmount: "amount",
    autoExtractDate: "date",
    autoExtractTax: "tax",
    autoExtractSummary: "Works across languages and receipt formats worldwide — without extra steps.",
    globalReadyTitle: "Global-ready",
    globalReadyDesc: "Receipts don't stop at borders.",
    globalReadyPoint1: "Multi-currency support",
    globalReadyPoint2: "Automatic currency conversion",
    globalReadyPoint3: "Clean, consistent records — always ready when you need them",
    multiOrgTitle: "Multiple Organizations",
    multiOrgDesc: "Manage multiple businesses, projects, or clients under one account.",
    multiOrgPoint1: "Keep expenses clearly separated",
    multiOrgPoint2: "Switch contexts without switching tools",
    teamAccessTitle: "Team access (when you need it)",
    teamAccessDesc: "Invite teammates to forward, review, and export receipts together.",
    teamAccessPoint1: "Role-based access",
    teamAccessPoint2: "Clear ownership",
    teamAccessPoint3: "No messy handoffs",
    easyExportTitle: "Easy Exports",
    easyExportDesc: "When it's time for accounting or taxes:",
    easyExportPoint1: "Export receipts and data as CSV or PDF",
    easyExportPoint2: "Share instantly with accountants or bookkeepers",
    easyExportPoint3: "No reformatting required",
    startSteps: {
      startFree: "Start free",
      startFreeTitle: "Create your space",
      startFreeDesc: "Set up your account in minutes with no payment information required.",
      mailFile: "Forward with gratitude",
      mailFileTitle: "Acknowledge the exchange",
      mailFileDesc: "Each receipt is a reminder that money supported your work, life, or growth.",
      support: "Let AI organize",
      supportTitle: "Release the admin",
      supportDesc: "We extract, categorize, and store the details without manual data entry.",
      oneAction: "Build awareness",
      oneActionTitle: "See your money clearly",
      oneActionDesc: "Over time, your receipts reveal patterns that help you make more intentional decisions."
    },
    startBlock: {
      label: "Begin the Practice",
      title: "A simple ritual for a more<br/><b>intentional financial life.</b>",
      desc: "Start free, create your forwarding address, and turn every receipt into a small moment of gratitude and awareness. Forward it once; Forward Receipts handles the organization automatically.",
      cta: "Start your ritual"
    },
    receiptReady: {
      label: "Effortless Awareness",
      title: "Make attention to money easy.",
      intro: "Forward receipts from any email account and keep your spending visible without turning financial awareness into another exhausting task.",
      items: [
        { title: "No complicated onboarding", desc: "Start quickly and let the habit fit naturally into your day." },
        { title: "Multiple inboxes, one practice", desc: "Forward receipts from every email account into one intentional system." },
        { title: "See each exchange as it happens", desc: "Keep your financial life current instead of avoiding it until tax time." },
        { title: "Automatic organization", desc: "Let technology handle the details while you stay connected to the meaning behind the money." }
      ]
    },
    autoDetails: {
      label: "Automatic Expense Details",
      title: "Honor the details without<br/>doing the busywork.",
      intro: "Forward Receipts reads each receipt automatically, preserving the information that helps you understand and respect every dollar moving through your business.",
      items: [
        { title: "Merchant and source", desc: "Remember where the money went." },
        { title: "Date and time", desc: "See when each financial exchange happened." },
        { title: "Amount and currency", desc: "Keep the value of every purchase visible." },
        { title: "Taxes and line items", desc: "Capture the complete financial picture automatically." }
      ]
    },
    globalReadyNew: {
      label: "Global Abundance",
      title: "Your money story crosses borders.",
      intro: "Whether you earn, spend, or travel internationally, Forward Receipts keeps every receipt connected to one clear view of your financial life.",
      items: [
        { title: "Receipts from any country", desc: "Keep every exchange, wherever it happens." },
        { title: "Multiple currencies", desc: "Understand money moving through different markets." },
        { title: "Automatic conversion", desc: "See consistent values without manual calculations." },
        { title: "Region-aware records", desc: "Keep practical financial details organized across locations." }
      ]
    },
    multipleOrganizations: {
      label: "Every Venture, One Vision",
      title: "Build wealth across every project.",
      intro: "Freelancers often manage multiple clients, projects, and income streams. Keep each one distinct while seeing how they contribute to the larger financial life you are creating.",
      items: [
        { title: "Separate expense streams", desc: "Give every project its own clear financial record." },
        { title: "Switch instantly", desc: "Move between ventures without losing perspective." },
        { title: "Consistent money visibility", desc: "Keep amounts and currencies easy to understand." },
        { title: "Unified reporting", desc: "See individual projects and the whole picture together." }
      ]
    },
    teamAccessNew: {
      label: "Shared Intention",
      title: "Build with people who respect the money.",
      intro: "Invite collaborators to forward, review, and organize receipts together so financial awareness becomes part of how your team operates.",
      items: [
        { title: "Thoughtful permissions", desc: "Give each person the access they genuinely need." },
        { title: "Meaningful annotations", desc: "Add context so every expense tells the full story." },
        { title: "Accountant-ready exports", desc: "Turn daily awareness into clean professional records." },
        { title: "Collaborative clarity", desc: "Keep everyone aligned around where the money goes." }
      ]
    },
    easyExportsNew: {
      label: "Peace at Tax Time",
      title: "Turn daily gratitude into long-term order.",
      intro: "Because you acknowledged and forwarded each receipt along the way, tax time becomes a handoff—not a reckoning.",
      items: [
        { title: "Export clean CSV, PDF, or tax data", desc: "Give your accountant organized records without a scramble." },
        { title: "Connect with your accounting tools", desc: "Carry your intentional money system into the rest of your workflow." },
        { title: "No last-minute formatting", desc: "Your records are already prepared because the habit happened daily." }
      ]
    }
  },
  promo: {
    freeTitle: "Begin for free",
    freeDesc: "Start your money ritual without a credit card or commitment.",
    easyTitle: "One simple action",
    easyDesc: "Forward each receipt while the purchase is still fresh in your mind.",
    supportTitle: "Built for freelancers",
    supportDesc: "Keep business spending visible without adding another administrative chore.",
    smartTitle: "Clarity that compounds",
    smartDesc: "Let AI organize the details so your attention can stay on better money decisions."
  },
  newsletter: {
    ritualKicker: "The Forward Receipts Philosophy",
    ctaTitle: "A daily ritual to <strong>attract wealth</strong>, receipt by receipt.",
    ctaDesc: "A receipt is evidence that money moved and value was exchanged. Forward it with intention, review without judgment, and turn daily awareness into wiser financial decisions.",
    ritualSteps: [
      {
        title: "Receive with gratitude",
        desc: "A receipt is evidence that money moved and value was exchanged. Pause for one breath and acknowledge what the purchase supported."
      },
      {
        title: "Forward with intention",
        desc: "Send the receipt to Forward Receipts. That small action says: I pay attention to my money, and I respect what flows through my life and business."
      },
      {
        title: "Review without judgment",
        desc: "As your receipts collect, patterns emerge. Use them for honest awareness, wiser decisions, and a clearer path to attract wealth over time."
      }
    ],
    tryFree: "Start your ritual",
    googlePlay: "Google Play",
    appStore: "App Store"
  },
  pricing: {
    kicker: "Choose Your Practice",
    mostPopular: "MOST POPULAR",
    sectionTitle: "Simple pricing for intentional growth.",
    sectionDesc: "Start with the level of support that fits your business today, then grow as your financial life expands.",
    starterPlan: {
      name: "STARTER Free",
      price: "$0",
      desc: "For freelancers beginning a daily money-awareness habit.",
      features: [
        "1 account",
        "Up to 100 receipts/month",
        "Email forwarding",
        "Basic categorization",
        "CSV export",
        "Standard support"
      ],
      cta: "Begin for free"
    },
    proPlan: {
      name: "Pro",
      price: "$9.99",
      period: "/month",
      desc: "For independent professionals ready to see and shape their financial patterns.",
      features: [
        "Up to 5 accounts",
        "1,000 receipts/month",
        "Shared inbox forwarding",
        "Smart categorization rules",
        "Advanced exports (CSV + PDF)",
        "Basic analytics dashboard",
        "Priority support"
      ],
      cta: "Start Pro"
    },
    scalePlan: {
      name: "SCALE",
      price: "$49",
      period: "/month",
      desc: "For growing teams building wealth with shared financial clarity.",
      features: [
        "Up to 50 accounts",
        "10,000 receipts/month",
        "Team permissions",
        "API access",
        "Multi-org management",
        "Custom forwarding aliases",
        "Slack + accounting integrations",
        "Advanced analytics"
      ],
      cta: "Start Scale"
    },
    enterprisePlan: {
      name: "ENTERPRISE",
      price: "Custom",
      period: "",
      desc: "For organizations making intentional money management part of how they operate.",
      features: [
        "Up to 1,000 accounts",
        "Unlimited receipts",
        "SSO + SAML",
        "Custom domain forwarding",
        "Dedicated onboarding",
        "Audit logs",
        "Enterprise-grade security",
        "SLA support"
      ],
      cta: "Contact us"
    }
  },
  faq: {
    sectionTitle: "Faq",
    kicker: "Questions Before You Begin",
    heading: "Frequently Asked Questions",
    contactPrefix: "Can't find an answer?",
    contactLink: "Email us",
    projectCtaTitle: "Start your project with Forward.",
    projectCtaDesc: "We will email you about the product.",
    emailPlaceholder: "Your Email",
    subscribe: "Subscribe",
    registerQ: "How do I register?",
    registerA: "Download the app and create your account in under a minute. Then forward your first receipt—a small first step toward a more intentional relationship with money.",
    uploadQ: "How do I upload a receipt?",
    uploadA: "Forward the receipt from any email account. You can also scan or upload one in the app. AI extracts and organizes the details automatically.",
    supportQ: "How do I contact support?",
    supportA: "Email us anytime at <a href='mailto:support@forwardreceipts.com'>support@forwardreceipts.com</a>. A real person will get back to you as soon as possible.",
    inviteQ: "How do I invite team members?",
    inviteA: "Add their email address, choose the access they need, and invite them to forward, review, and organize receipts with you.",
    freeQ: "Is the app free?",
    freeA: "Yes. Begin with the free plan and upgrade only when you need more receipt volume, accounts, or collaboration features.",
    changePlanQ: "How do I change my plan?",
    changePlanA: "Open Settings, choose the plan that fits your needs, and confirm the change. You can upgrade or downgrade at any time."
  },
  privacy: {
    title: "Privacy Policy",
    policyIntro:
      'At Impulz Digital Pte Ltd, Inc ("We", "Our"), our most important asset is our relationship with our user community. We are committed to safeguarding your privacy online. This privacy policy ("Privacy Policy" or "Policy") describes how we collect, use, disclose, share and secure the personal and company information you provide when you use our expense management, invoicing or bill processing software, through our mobile application (the "Forwardreceipts Application"). It also describes your choices regarding the use, access and correction of your Personal Data.',
    userDefinition:
      'In this Privacy Policy, "you" and "your" refers to individual users of the Forwardreceipts Application, as well as to Members and Corporate Members.',
    processDataNotice:
      "We process your Personal Data as set out in the Privacy Policy which you should read.",
    additionalRightsNotice:
      "If you are located in the EU/UK, California (USA), or Singapore, you may have additional rights under applicable data protection laws. Please review this Privacy Policy carefully to understand your rights.",
    section1Title: "1. What Data We Collect?",
    personalDataDef:
      'Personal information or "Personal Data", means any information about an individual from which that person can be identified, or which when combined with other information which is in the possession of, or is likely to come into the possession of, Forwardreceipts Application could be used to identify that person. Forwardreceipts Application will not use your Personal Data except as set forth in this Privacy Policy and in the Terms of Service.',
    collectionOverview:
      "We may collect (both directly and indirectly), use, store and transfer different kinds of personal data about you. For specific details about how Forwardreceipts Application does this with cookies, identifiers and other tracking technologies please review the Forwardreceipts Application Cookie Policy below. The categories of Personal Data we collect, use, store and transfer have been grouped together as follows:",
    labelDataYouProvide: "Data You Provide. ",
    descDataYouProvide:
      "Account registration information (such as name, email address, and phone number), uploaded receipts/images, and payment or expense records.",
    labelAutoCollectedData: "Automatically Collected Data. ",
    descAutoCollectedData:
      "Device information (including operating system and device ID), usage logs, and cookies/tracking data.",
    labelThirdPartyData: "Third-Party Data (if applicable). ",
    descThirdPartyData:
      "Relevant data provided by payment gateways or analytics service providers.",
    specialCategoryNotice:
      'We do not collect any "Special Categories of Personal Data" about you (this includes details about your race or ethnicity, religious or philosophical beliefs, sex life, sexual orientation, political opinions, trade union membership, information about your health, and genetic and biometric data) nor do we collect any information about criminal convictions and offenses.',
    section2Title: "2. How We Use Your Data?",
    useDataIntro:
      "Forwardreceipts Application and our Partner Companies may use your Personal Data in the following ways:",
    useDataAccount:
      "to facilitate the creation of and secure your account on our network;",
    useDataIdentify: "identify you as a Member or Non-Member in our system;",
    useDataAdmin:
      "to administer and provide improved administration of the Forwardreceipts Service;",
    useDataExperience:
      "to improve the quality of experience when you interact with the Forwardreceipts Service, including staff training;",
    useDataWelcome:
      "to send you a welcome email to verify ownership of the email address provided when your account was created;",
    useDataNotif:
      "to send you administrative email and/or chat notifications, such as security or support and maintenance advisories;",
    useDataFees: "to collect fees and payments owing to us;",
    useDataInquiries:
      "to respond to your inquiries related to employment opportunities or other requests and to resolve disputes;",
    useDataFeatures:
      "to provide you with access to and information about customized features, new functionality, and partner integrations;",
    useDataEligibility:
      "to determine your eligibility to use the various programs part of the Forwardreceipts Service;",
    useDataNewsletters:
      "to provide you with hardcopy or electronic newsletters, or surveys;",
    useDataBusiness:
      "to operate our business, including to process payment transactions, manage and enforce contracts with you or with third parties, manage our corporate governance, compliance and auditing practices, and generate anonymized or aggregated data;",
    useDataFraud:
      "to prevent and identify fraud and other illegal activity including but not limited to making telephone calls to you, from time to time, as a part of secondary fraud protection or to solicit your feedback;",
    useDataIdentity:
      "to verify your identity as part of compliance with requirements of Partner Companies or applicable regulations;",
    useDataDisputes:
      "to resolve disputes and protect the rights of Members and third parties;",
    useDataLegal:
      "to respond to claims and legal process (such as subpoenas and court orders);",
    useDataCompliance:
      "to monitor and enforce compliance with the applicable Terms of Service;",
    useDataStopIllegal:
      "to prevent or stop any activity that may be illegal, unethical, or legally actionable;",
    useDataAccuracy:
      "to compare information provided by you for accuracy and verification with third parties;",
    useDataSupport:
      "to provide you with support and to respond to your inquiries, including to investigate and address your concerns and monitor and improve our responses and responding to consumer rights requests;",
    useDataOther:
      "as otherwise described to you when collecting your personal information; directed by you; needed to comply with laws; and",
    useDataMerger:
      "to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which personal information held by us about our consumers is among the assets transferred.",
    essentialComm:
      "We may use your Personal Data to send you essential service-related communications, such as purchase confirmations, policy updates, or changes to our Terms and Conditions. These notices are necessary for the proper use of our services. Where permitted by law, if you opt out of receiving such communications, Forwardreceipts reserves the right to discontinue service. If you choose to provide feedback about the Forwardreceipts Service, we may use that feedback for business purposes and will handle any Personal Data included in accordance with this Privacy Policy.",
    publicInfoNotice:
      "Any information, including Personal Data, that you elect to make publicly available through the Forwardreceipts Service may be visible to other Members or the public. Even if you later remove such information, it may remain accessible in cached or archived versions of the service, or if it has been copied or stored by other Members. In certain cases, we may also process Personal Data provided by our Corporate Members. In these situations, we do not have a direct relationship with the affected individuals. If you believe your Personal Data has been collected in this way and wish to be removed, please contact the relevant Corporate Member directly.",
    pushNotifNotice:
      "We may also use your Personal Data to send push notifications about events, promotions, or service updates. If you prefer not to receive these communications, you may disable them in your device settings. To deliver these notifications, we may collect limited device information, such as operating system and device identifiers, solely for notification purposes.",
    section3Title: "3. Legal Basis for Processing Personal Data",
    legalBasisIntro:
      "We process your personal data only when we have a valid legal basis under applicable data protection laws. Depending on your jurisdiction, the legal basis for processing may vary. This section outlines the legal bases we rely upon and explains how they apply to different regions.",
    section3_1Title:
      "3.1 European Union / United Kingdom (GDPR, Regulation (EU) 2016/679)",
    gdprIntro:
      "In accordance with the General Data Protection Regulation (GDPR, Regulation (EU) 2016/679), we process personal data based on the following legal grounds:",
    gdprContractTitle: "a) Performance of a Contract (Article 6(1)(b) GDPR)",
    gdprContractDesc:
      "We process your personal data as necessary to fulfill contractual obligations or to provide the services you have requested. This includes, but is not limited to:",
    gdprContractAccount:
      "Creating and managing your account, and providing secure access to the app.",
    gdprContractExpenses:
      "Recording, storing, and processing your expenses, receipts, and payment information.",
    gdprContractPayments:
      "Facilitating payments, transfers, or other financial operations related to the service.",
    gdprContractOutro:
      "Without such processing, we would be unable to deliver the core functionalities of our application or meet our contractual commitments.",
    gdprConsentTitle: "b) Consent (Article 6(1)(a) GDPR & Article 7 GDPR)",
    gdprConsentDesc:
      "Certain processing activities require your explicit consent, particularly those not strictly necessary for providing the service. Examples include:",
    gdprConsentMarketing:
      "Receiving marketing communications, newsletters, or promotional notifications.",
    gdprConsentTracking:
      "Use of cookies, tracking technologies, or analytics tools that are not essential to the service.",
    gdprConsentOutro:
      "Consent must be freely given, specific, informed, and unambiguous. You have the right to withdraw consent at any time. Withdrawal will not affect the lawfulness of processing based on consent prior to withdrawal.",
    gdprInterestTitle: "c) Legitimate Interests (Article 6(1)(f) GDPR)",
    gdprInterestDesc:
      "We may also process personal data when necessary for our legitimate interests, provided that such interests are not overridden by your rights and freedoms. Legitimate interests generally include:",
    gdprInterestImprove: "Improving and optimizing our products and services.",
    gdprInterestSecurity:
      "Ensuring the security, integrity, and availability of the app and associated systems.",
    gdprInterestFraud:
      "Preventing unauthorized access, fraud, or other malicious activity.",
    gdprInterestOutro:
      "We perform a balancing test to ensure our legitimate interests do not disproportionately affect your privacy rights. You may object to this processing at any time by contacting our data protection team.",
    nonEULegalBasisNote:
      "For users located outside the EU/UK, the legal bases outlined above may not be legally mandatory under local law. However, we apply the same principles as a matter of transparency and best practice, to ensure that you are informed about how your personal data is processed, stored, and used.",
    section3_2Title: "3.2 United States - California (CCPA / CPRA)",
    ccpaIntro:
      "California residents have specific rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), which may include:",
    ccpaRightKnow:
      "The right to know what personal information is collected, used, shared, or sold.",
    ccpaRightDelete:
      "The right to request deletion of personal information collected by us.",
    ccpaRightOptOut:
      "The right to opt-out of the sale or sharing of personal information for targeted advertising purposes.",
    ccpaRightDiscrim:
      "The right to non-discrimination for exercising privacy rights.",
    ccpaOutro:
      "We process California residents’ personal data in accordance with these rights and maintain procedures to respond to such requests.",
    section3_3Title: "3.3 Singapore (PDPA)",
    pdpaIntro:
      "Under the Personal Data Protection Act (PDPA) of Singapore, you are entitled to certain rights regarding your personal data, including:",
    pdpaRightInformed:
      "The right to be informed of the purposes for which your personal data is collected, used, or disclosed.",
    pdpaRightAccess:
      "The right to access and correct personal data held by us.",
    pdpaRightWithdraw:
      "The right to withdraw consent for the collection, use, or disclosure of personal data, where applicable.",
    pdpaOutro:
      "We will handle requests related to these rights in accordance with the PDPA requirements.",
    section4Title: "4. How We Share Data",
    shareDataIntro:
      "We value your privacy and are committed to handling your Personal Data responsibly. We only share your Personal Data in the limited circumstances described below, and always in accordance with applicable laws.",
    section4_1Title: "4.1 Service Providers and Business Partners",
    serviceProviderIntro:
      "We may share your Personal Data with trusted third-party service providers and business partners that assist us in operating our services and fulfilling our contractual obligations. These providers may include, but are not limited to:",
    labelCloud: "Cloud hosting and storage providers ",
    descCloud: "(for secure storage and hosting of applications and data);",
    labelPayment: "Payment processors ",
    descPayment:
      "(to facilitate transactions, reimbursements, payroll, or other financial operations);",
    labelAnalytics: "Analytics providers ",
    descAnalytics:
      "(to help us better understand usage trends and improve our products and services);",
    labelIT: "IT and security providers ",
    descIT:
      "(for system maintenance, technical support, and fraud prevention).",
    serviceProviderOutro:
      "These service providers are granted access to your Personal Data solely for the purpose of performing services on our behalf and are contractually bound not to disclose, use, or retain your information for any other purpose.",
    section4_2Title: "4.2 Legal and Regulatory Requirements",
    legalReqDesc:
      "We may disclose your Personal Data when required to do so by law, regulation, or legal process, or in response to valid requests by public authorities, including to comply with national security or law enforcement requirements. Specifically, this may include responding to subpoenas, court orders, warrants, or other lawful requests from competent governmental or regulatory bodies.",
    legalReqFaithIntro:
      "We may also disclose Personal Data when we believe, in good faith, that such disclosure is necessary to:",
    legalReqProtect:
      "Protect and defend our rights, property, or the safety of our users;",
    legalReqInvestigate:
      "Prevent or investigate potential wrongdoing in connection with our services;",
    legalReqLiability: "Protect against legal liability.",
    section4_3Title: "4.3 No Sale of Personal Data",
    noSaleDesc:
      'We do not sell, rent, or trade your Personal Data to third parties. For residents of California, in compliance with the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), we confirm that we do not engage in the "sale" or "sharing" of Personal Data as those terms are defined under California law.',
    noSaleChangeNotice:
      'Nevertheless, if our practices were to change in the future, we will update this Privacy Policy accordingly and provide you with a clear and conspicuous "',
    doNotSellLink: "Do Not Sell or Share My Personal Information",
    noSaleOutro:
      '" link on our website or application, as required under California law.',
    section4_4Title: "4.4 Aggregated or De-Identified Data",
    aggDataDesc:
      "We may share aggregated or de-identified information that cannot reasonably be used to identify you. Such data may be used for research, analytics, marketing, or business development purposes, and may be shared with third parties without restriction.",
    section5Title: "5. International Data Transfers",
    transferIntro:
      'Your Personal Data may be stored, processed, and maintained in countries outside of your country of residence, including but not limited to Singapore, the United States, and countries outside the European Economic Area ("EEA"). By using our Services, you acknowledge and consent to the transfer of your Personal Data to countries that may not have data protection laws equivalent to those in your jurisdiction.',
    gdprTransferNotice:
      "For users located in the EU/UK, Expensify ensures that such cross-border transfers comply with applicable data protection laws, including the General Data Protection Regulation (GDPR). Specifically, we implement one or more of the following legal mechanisms to protect your Personal Data during international transfers:",
    labelSCC: "Standard Contractual Clauses (SCCs): ",
    descSCC:
      "We rely on the European Commission-approved SCCs as a legal basis for transferring personal data outside the EEA, ensuring that appropriate safeguards and contractual obligations are in place to protect your rights and personal information.",
    labelAdequacy: "Adequacy Decisions: ",
    descAdequacy:
      "Where applicable, we transfer data only to countries deemed by the European Commission or relevant authorities to provide an adequate level of data protection.",
    labelSafeguards: "Other Appropriate Safeguards: ",
    descSafeguards:
      "In some cases, we may implement additional measures such as binding corporate rules, data transfer agreements, or other safeguards recognized under applicable law to ensure your data is protected to a level consistent with GDPR requirements.",
    partnerContractNotice:
      "We also ensure that our partners and service providers who process Personal Data on our behalf are contractually obligated to maintain the confidentiality, integrity, and security of your data in accordance with this Privacy Policy and applicable law.",
    nonEUTransferNotice:
      "For users outside the EU/UK, including Singapore, the United States, and other jurisdictions, this clause provides transparency regarding potential international data transfers. While specific legal safeguards may differ depending on local law, we remain committed to protecting your Personal Data and implementing reasonable security measures during transfer and storage.",
    transferConsent:
      "By using our Services, you consent to these transfers and the measures described above to protect your Personal Data.",
    section6Title: "6. Data Retention",
    retentionIntro:
      "Forwardreceipts retains Personal Data only for as long as necessary to fulfill the purposes for which it was collected, as described in this Privacy Policy, and to comply with applicable legal, accounting, and regulatory requirements.",
    retentionDeletionNotice:
      "When you delete your individual account, all Personal Data associated with that account will be permanently deleted within ",
    retentionDays: "[30] ",
    retentionDeletionSuffix:
      "days, unless retention is required by law or for legitimate business purposes. Find more details about account deletion ",
    retentionLabelHere: "here",
    corporateDataNotice:
      "Please note that any receipts or related data you have uploaded to a company or organization account will remain accessible to that organization as part of its corporate financial records. Such retained data may be anonymized or de-identified where applicable to minimize any link to your personal identity.",
    retentionPurposeIntro:
      "Forwardreceipts may retain and process limited data necessary to:",
    retentionLegal:
      "Comply with legal obligations, dispute resolution, and enforcement of agreements.",
    retentionAudit:
      "Maintain audit logs or transactional records as required by law or regulation.",
    retentionCorporate:
      "Fulfill data retention requirements for our Corporate Members in accordance with the Forwardreceipts Terms of Service.",
    secureDeletion:
      "We use secure deletion and de-identification methods to ensure that Personal Data is permanently and safely destroyed when no longer required.",
    corporateContactNotice:
      "If you are affiliated with a Corporate Member, please contact your organization directly for details regarding its specific data retention and management policies for member-generated content.",
    section7Title: "7. Choice/Opt-out",
    choiceOptOutDesc:
      "Expensify offers you the choice of receiving different types of communication and information related to our company, products and services. You may subscribe to e-newsletters or other publications; you may also elect to receive marketing communications and other special offers from us via email or SMS messaging. You may also opt-in to communicate with other Members and Non-Members via SMS messaging. If at any time you would like to change your communication preferences, we provide unsubscribe links and an opt-out mechanism for your convenience where available. You may also access and manage your preferences from your account.",
    section8Title: "8. Cookie Policy and Use of Tracking Technologies",
    cookieIntro:
      'When you interact with our Site or Application, we aim to make your experience simple, efficient, and personalized. To achieve this, we and our trusted partners use industry-standard tracking technologies, including cookies, pixels, web beacons, flash cookies, mobile identifiers, and similar tools (collectively referred to as "cookies" or "tracking technologies"). By using our Services, you consent to the use of these technologies as described in this policy.',
    section8_1Title: "8.1 What Are Cookies and How Long Do They Last?",
    cookieDef:
      "Cookies are small text files or data fragments stored on your browser or device when you visit a website or use a mobile application. They allow us to store and sometimes track information about your use of our Services.",
    sessionCookies:
      "Session cookies last only during your browsing session and expire once you close your browser or exit the application.",
    persistentCookies:
      "Persistent cookies remain until they expire or are deleted by you or your browser.",
    partyCookies:
      "First-party cookies are set directly by us, while third-party cookies are placed by external providers (e.g., analytics or advertising partners). These third parties may recognize your device across multiple websites or applications.",
    section8_2Title: "8.2 Other Tracking Technologies",
    otherTrackingIntro: "In addition to cookies, we may also use:",
    webBeacons:
      'Web Beacons (or "clear GIFs") – small graphics embedded in our content that help us measure engagement and usage.',
    flashCookies:
      "Flash Cookies / Local Shared Objects (LSOs) – used to store settings and usage data.",
    mobileIds:
      "Mobile Identifiers – such as Apple’s IDFA or Android’s UDID, to enable app-related analytics, advertising, and personalization.",
    section8_3Title: "8.3 How We Use Cookies",
    useCookiesIntro: "We use cookies and similar technologies to:",
    useCookiesOperate: "Operate and secure our Site and Application;",
    useCookiesRecognize:
      "Recognize you when you return and personalize your experience;",
    useCookiesImprove:
      "Understand usage trends and improve functionality, performance, and service delivery;",
    useCookiesAds:
      "Provide tailored advertising and measure the effectiveness of such advertising (interest-based advertising);",
    useCookiesAnalytics:
      "Enable analytics and troubleshooting by linking usage information to your account, where applicable.",
    cookieCategoriesIntro:
      "Cookies we use generally fall into the following categories:",
    labelNecessary: "Strictly Necessary Cookies ",
    descNecessary:
      "– essential for core functions such as account login, secure areas, transactions, or billing.",
    labelPerformance: "Performance/Analytics Cookies ",
    descPerformance:
      "– measure visitor activity, site navigation, and engagement, helping us optimize our Services.",
    labelFunctionality: "Functionality Cookies ",
    descFunctionality:
      "– remember your preferences (e.g., language, region) and enable social media plug-ins or widgets.",
    labelTargeting: "Targeting/Advertising Cookies ",
    descTargeting:
      "– record visits and browsing behavior to deliver more relevant ads and track ad performance.",
    section8_4Title: "8.4 Your Choices",
    yourChoicesIntro:
      "You have control over the use of cookies and tracking technologies:",
    section8_4_1Title: "8.4.1 Browser and Device Settings",
    browserSettingsDesc:
      "Most browsers and mobile devices automatically accept cookies, but you can modify settings to block or notify you when cookies are placed. Guidance for major browsers (e.g., Chrome, Firefox, Safari, Microsoft Edge) is available online. Blocking cookies may limit certain functionality of our Services.",
    section8_4_2Title: "8.4.2 Behavioral Advertising",
    behavioralAdsDesc:
      "We may partner with third parties to deliver interest-based advertising. These providers may use cookies to collect information about your browsing activities across different websites. You may opt out of such practices through resources such as:",
    naiOptOut: "Network Advertising Initiative opt-out",
    daaOptOut: "Digital Advertising Alliance opt-out",
    euCookieLink: "For EU/UK users: cookie preference tools available [link].",
    nonTargetedAdsNotice:
      "Please note that you will continue to see general (non-targeted) advertising even after opting out.",
    section8_4_3Title: "8.4.3 Analytics",
    analyticsDesc:
      "We use analytics services (e.g., Google Analytics) and mobile analytics software to understand usage and improve functionality. These tools may record events, performance data, and aggregated trends but are not linked to personally identifiable information. You can opt out of Google Analytics by installing the [Google Analytics Opt-out Browser Add-on].",
    section8_5Title: "8.5 Do Not Track (DNT)",
    dntDesc:
      'Some browsers include a "Do Not Track" setting. Currently, due to the absence of a uniform industry standard, we do not respond to DNT signals.',
    section9Title: "9. Your Rights",
    rightsIntro:
      "Forwardreceipts recognizes that users have certain rights regarding the collection, use, and processing of their Personal Data. These rights vary depending on your jurisdiction:",
    pdpaRightsTitle:
      "For users in Singapore (under the Personal Data Protection Act, PDPA):",
    pdpaRightsDesc:
      "You have the right to access your Personal Data held by Forwardreceipts, request corrections to any inaccurate or incomplete information, and withdraw your consent for the collection, use, or disclosure of your Personal Data, subject to any legal or contractual restrictions.",
    ccpaRightsTitle:
      "For users in the United States (under the California Consumer Privacy Act / California Privacy Rights Act, CCPA/CPRA):",
    ccpaRightsDesc:
      'You have the right to know what Personal Data we collect, use, share, or sell. You may request the deletion of your Personal Data, subject to certain exceptions provided under applicable law. If applicable, you may also exercise your right to opt out of the sale of your Personal Data by using the "Do Not Sell My Info" link provided in this Privacy Policy.',
    gdprRightsTitle:
      "For users in the European Union or the United Kingdom (under the General Data Protection Regulation, GDPR / UK GDPR):",
    gdprRightsDesc:
      "You have the right to access your Personal Data, request corrections to inaccuracies, request deletion of your Personal Data, and request restrictions on the processing of your Personal Data. You also have the right to data portability and to object to certain processing activities, including marketing-related processing, based on legitimate interests or consent.",
    allUsersRightsTitle: "For all users, regardless of location:",
    allUsersRightsDesc:
      "You may request the deletion of your Forwardreceipts account and all associated Personal Data, or request a portable copy of your Personal Data in a structured, commonly used, and machine-readable format. Forwardreceipts will take reasonable steps to respond to such requests in accordance with applicable law.",
    exerciseRightsNotice:
      "To exercise any of these rights, please contact us using the contact information provided in this Privacy Policy. We may require verification of your identity before fulfilling your request to ensure the protection of your Personal Data.",
    section10Title: "10. Security",
    securityIntro:
      "Forwardreceipts takes the protection of your Personal Data seriously and implements a range of industry-standard technical, administrative, and organizational measures to safeguard it against unauthorized access, use, disclosure, alteration, or destruction.",
    securityAuthNotice:
      "Access to your account requires authentication, such as a password or other secure sign-on mechanisms, which you should keep confidential and not share with unauthorized individuals. While we strive to maintain the highest level of security, no method of data transmission or storage can guarantee absolute protection.",
    securityContactNotice:
      "If you have any concerns or questions regarding the security of your Personal Data, please contact us at support@forwardreceipts.com. Forwardreceipts will review and respond to your inquiries in accordance with applicable privacy and data protection laws.",
    section11Title: "11. Children’s Privacy",
    childrenIntro:
      "The Forwardreceipts Service and Application are not intended for use by children under the age of 13, or under the minimum age required by the applicable jurisdiction. We do not knowingly collect Personal Data from children below this age threshold.",
    childrenDeleteNotice:
      "If we become aware that we have inadvertently collected Personal Data from a child under the applicable age, we will take immediate steps to delete such information from our systems. Parents or legal guardians who believe that their child has provided Personal Data through the Forwardreceipts Service or Application are encouraged to contact us at support@forwardreceipts.com, so that we can promptly remove the data.",
    childrenCompliance:
      "We comply with applicable children’s privacy laws, including the Children’s Online Privacy Protection Act (COPPA) in the United States, and similar legislation in other jurisdictions. No child under the relevant age should submit personal information without parental or guardian consent.",
    childrenParentalMonitoring:
      "Forwardreceipts also encourages parents and guardians to actively monitor their children’s online activity and to help enforce these privacy protections.",
    section12Title: "12. Subscription and Billing",
    section12_1Title: "12.1 Subscription Services",
    subServicesDesc:
      "Our app may offer paid subscription plans that provide access to additional features or premium content. Each subscription plan clearly states its price, billing period, and renewal terms before purchase.",
    subPlatformNotice:
      "If you subscribe through the Apple App Store or Google Play Store, the payment and renewal process is handled entirely by the respective platform, in accordance with their terms and policies.",
    section12_2Title: "12.2 Auto-Renewal",
    autoRenewalDesc:
      "All recurring subscriptions are set to automatically renew at the end of each billing period, unless canceled at least 24 hours before the renewal date. The renewal will be charged to your Apple ID account or Google Play account using the payment method you selected at the time of purchase.",
    priceChangeNotice:
      "You will be notified by Apple or Google of any price changes in advance, and the renewal will proceed at the new rate unless you cancel the subscription before the next billing cycle.",
    section12_3Title: "12.3 Managing and Canceling Subscriptions",
    manageSubIntro: "You can manage or cancel your subscription at any time:",
    appleSubLabel:
      "Apple users: Go to Settings > [Your Name] > Subscriptions or visit",
    googleSubLabel:
      "Google Play users: Go to Google Play > Account > Subscriptions or visit",
    cancelSubEffect:
      "If you cancel, you will retain access to the premium features until the end of the current billing period. After that, your subscription will not renew automatically, and no further charges will apply.",
    section12_4Title: "12.4 Refunds",
    refundsDesc:
      "All payments and refunds are processed by Apple or Google according to their respective refund policies. We do not process refunds directly.",
    refundsInquiry: "For refund requests, please visit:",
    labelApple: "Apple: ",
    labelGoogle: "Google Play: ",
    section12_5Title: "12.5 Compliance and Data Handling",
    noPaymentStorage:
      "We do not directly store or have access to your full payment information. All transactions are securely processed by Apple or Google under their respective privacy and data protection frameworks.",
    subDataNotice:
      "Any information we receive (such as purchase confirmations or subscription status) is handled in accordance with this Privacy Policy and applicable data protection laws, including the GDPR and CCPA.",
    subMetadataNotice:
      "We only retain non-sensitive transactional metadata (e.g., subscription type, start and end date) for service continuity, analytics, and customer support purposes.",
    section13Title: "13. Contact Us",
    contactIntro:
      "If you have any comments, questions, or complaints regarding this Privacy Policy, or if you believe that Forwardreceipts has failed to meet its obligations in handling, using, or disclosing your Personal Data, you may contact us at support@forwardreceipts.com. We will review and respond to your inquiries in accordance with applicable privacy and data protection laws.",
    contactPIINotice:
      "You acknowledge and agree that when contacting Forwardreceipts—whether by email, chat, or other means—you should not include any personally identifiable information in your communications unless necessary. If such information is included, Forwardreceipts shall have no legal obligation or liability regarding the information disclosed in that communication.",
  },
};
